import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Calendar, BarChart3, Search, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, Clock, XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  active: { bg: 'bg-emerald-500', text: 'text-emerald-700', label: 'Active' },
  expiring_60: { bg: 'bg-amber-400', text: 'text-amber-700', label: '< 60 days' },
  expiring_soon: { bg: 'bg-orange-500', text: 'text-orange-700', label: '< 30 days' },
  expired: { bg: 'bg-red-500', text: 'text-red-700', label: 'Expired' },
};

function GanttTimeline({ data, search }) {
  const today = new Date();
  const [monthOffset, setMonthOffset] = useState(0);
  const monthsToShow = 12;

  const months = useMemo(() => {
    const m = [];
    for (let i = 0; i < monthsToShow; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + monthOffset + i, 1);
      m.push(d);
    }
    return m;
  }, [monthOffset]);

  const startDate = months[0];
  const endDate = new Date(months[months.length - 1].getFullYear(), months[months.length - 1].getMonth() + 1, 0);
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

  const getPosition = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const days = Math.ceil((d - startDate) / (1000 * 60 * 60 * 24));
    return Math.max(0, Math.min(100, (days / totalDays) * 100));
  };

  const todayPos = getPosition(today.toISOString().split('T')[0]);

  const filtered = data.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.discipline?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="gantt-timeline">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMonthOffset(o => o - 3)}><ChevronLeft size={14} /></Button>
          <span className="text-sm font-medium min-w-[200px] text-center">
            {months[0].toLocaleString('default', { month: 'short', year: 'numeric' })} — {months[months.length - 1].toLocaleString('default', { month: 'short', year: 'numeric' })}
          </span>
          <Button variant="outline" size="sm" onClick={() => setMonthOffset(o => o + 3)}><ChevronRight size={14} /></Button>
          <Button variant="ghost" size="sm" onClick={() => setMonthOffset(0)}>Today</Button>
        </div>
        <div className="flex gap-3">
          {Object.entries(STATUS_STYLES).map(([key, s]) => (
            <div key={key} className="flex items-center gap-1.5 text-xs">
              <div className={`w-3 h-3 rounded-sm ${s.bg}`} />
              <span className="text-muted-foreground">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        {/* Month headers */}
        <div className="flex border-b bg-muted/30 sticky top-0 z-10">
          <div className="w-48 shrink-0 border-r px-3 py-2 text-xs font-medium text-muted-foreground">Therapist</div>
          <div className="flex-1 flex relative">
            {months.map((m, i) => (
              <div key={i} className="flex-1 text-center text-xs py-2 border-r border-border/50 text-muted-foreground">
                {m.toLocaleString('default', { month: 'short' })}
              </div>
            ))}
          </div>
        </div>

        {/* Therapist rows */}
        <div className="max-h-[500px] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">No therapists match your search.</div>
          )}
          {filtered.map((therapist) => (
            <div key={therapist.personnel_id} className="flex border-b last:border-b-0 hover:bg-muted/10" data-testid={`gantt-row-${therapist.personnel_id}`}>
              <div className="w-48 shrink-0 border-r px-3 py-2">
                <p className="text-sm font-medium truncate">{therapist.name}</p>
                <p className="text-[10px] text-muted-foreground">{therapist.discipline || 'N/A'}</p>
              </div>
              <div className="flex-1 relative py-1.5 min-h-[40px]">
                {/* Today line */}
                {todayPos !== null && (
                  <div className="absolute top-0 bottom-0 w-px bg-red-400 z-10" style={{ left: `${todayPos}%` }} />
                )}
                {/* Credential bars */}
                {therapist.credentials.map((cred, idx) => {
                  const endPos = cred.end_date ? getPosition(cred.end_date) : 100;
                  const startPos = cred.start_date ? getPosition(cred.start_date.split('T')[0]) : 0;
                  if (endPos === null && startPos === null) return null;
                  const left = Math.max(0, startPos || 0);
                  const width = Math.max(2, (endPos || 100) - left);
                  const style = STATUS_STYLES[cred.status] || STATUS_STYLES.active;

                  return (
                    <div
                      key={cred.doc_id}
                      className={`absolute h-5 rounded-sm ${style.bg} opacity-80 hover:opacity-100 cursor-pointer group`}
                      style={{ left: `${left}%`, width: `${width}%`, top: `${(idx % 3) * 7 + 4}px` }}
                      title={`${cred.type}: ${cred.end_date || 'No expiry'} (${cred.status})`}
                    >
                      <span className="text-[9px] text-white px-1 truncate block leading-5">{cred.type}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CalendarHeatmap({ calendarData }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());

  const months = useMemo(() => {
    const m = [];
    for (let i = 0; i < 12; i++) {
      const monthKey = `${year}-${String(i + 1).padStart(2, '0')}`;
      const entry = calendarData.find(d => d.month === monthKey);
      m.push({
        month: i,
        label: new Date(year, i, 1).toLocaleString('default', { month: 'short' }),
        count: entry?.count || 0,
        expired: entry?.expired || 0,
        upcoming: entry?.upcoming || 0,
        items: entry?.items || [],
      });
    }
    return m;
  }, [year, calendarData]);

  const maxCount = Math.max(1, ...months.map(m => m.count));
  const [selected, setSelected] = useState(null);

  const getHeatColor = (count, expired) => {
    if (count === 0) return 'bg-muted/40';
    if (expired > 0) return 'bg-red-100 border-red-300';
    const intensity = Math.min(count / maxCount, 1);
    if (intensity > 0.6) return 'bg-amber-200 border-amber-400';
    if (intensity > 0.3) return 'bg-amber-100 border-amber-300';
    return 'bg-yellow-50 border-yellow-200';
  };

  return (
    <div data-testid="calendar-heatmap">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setYear(y => y - 1)}><ChevronLeft size={14} /></Button>
          <span className="text-sm font-semibold min-w-[60px] text-center">{year}</span>
          <Button variant="outline" size="sm" onClick={() => setYear(y => y + 1)}><ChevronRight size={14} /></Button>
        </div>
        <div className="flex gap-3 text-xs">
          <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-muted/40 border" /> None</span>
          <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-yellow-50 border border-yellow-200" /> Low</span>
          <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-amber-200 border border-amber-400" /> High</span>
          <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-100 border border-red-300" /> Has Expired</span>
        </div>
      </div>

      <div className="grid grid-cols-4 md:grid-cols-6 gap-3 mb-4">
        {months.map((m) => (
          <button
            key={m.month}
            onClick={() => setSelected(selected === m.month ? null : m.month)}
            className={`p-3 rounded-lg border text-center transition-all hover:shadow-md ${getHeatColor(m.count, m.expired)} ${selected === m.month ? 'ring-2 ring-primary' : ''}`}
            data-testid={`heatmap-month-${m.month}`}
          >
            <p className="text-xs font-medium text-muted-foreground">{m.label}</p>
            <p className="text-xl font-bold mt-1">{m.count}</p>
            <div className="flex justify-center gap-2 mt-1">
              {m.expired > 0 && <span className="text-[10px] text-red-600">{m.expired} exp</span>}
              {m.upcoming > 0 && <span className="text-[10px] text-amber-600">{m.upcoming} due</span>}
            </div>
          </button>
        ))}
      </div>

      {selected !== null && months[selected]?.items.length > 0 && (
        <Card className="p-4" data-testid="heatmap-details">
          <h4 className="text-sm font-semibold mb-2">{months[selected].label} {year} — {months[selected].count} expirations</h4>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {months[selected].items.map((item, idx) => (
              <div key={idx} className={`flex items-center justify-between p-2 rounded text-sm ${item.is_past ? 'bg-red-50' : 'bg-amber-50'}`}>
                <div className="flex items-center gap-2">
                  {item.is_past ? <XCircle size={14} className="text-red-500" /> : <Clock size={14} className="text-amber-500" />}
                  <span className="font-medium">{item.therapist}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">{item.type}</span>
                  <Badge className={`text-[10px] ${item.is_past ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{item.date}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export default function CredentialTimeline() {
  const [view, setView] = useState('gantt');
  const [timelineData, setTimelineData] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [timelineRes, calendarRes] = await Promise.all([
        axios.get(`${API}/credentials/timeline`, { headers }),
        axios.get(`${API}/credentials/expiry-calendar`, { headers }),
      ]);
      setTimelineData(timelineRes.data);
      setCalendarData(calendarRes.data.monthly || []);
    } catch { toast.error('Failed to load credential data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Summary stats
  const stats = useMemo(() => {
    let total = 0, expired = 0, expiringSoon = 0, active = 0;
    timelineData.forEach(t => {
      t.credentials.forEach(c => {
        total++;
        if (c.status === 'expired') expired++;
        else if (c.status === 'expiring_soon') expiringSoon++;
        else active++;
      });
    });
    return { total, expired, expiringSoon, active, therapists: timelineData.length };
  }, [timelineData]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="credential-timeline-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 size={24} /> Credential Compliance Timeline</h1>
          <p className="text-muted-foreground text-sm mt-1">Visual overview of credential validity and upcoming expirations.</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-4 text-center" data-testid="stat-total">
          <p className="text-xs text-muted-foreground">Total Credentials</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </Card>
        <Card className="p-4 text-center" data-testid="stat-therapists">
          <p className="text-xs text-muted-foreground">Therapists</p>
          <p className="text-2xl font-bold text-blue-600">{stats.therapists}</p>
        </Card>
        <Card className="p-4 text-center" data-testid="stat-active">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1"><CheckCircle size={12} className="text-emerald-500" /> Active</p>
          <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
        </Card>
        <Card className="p-4 text-center" data-testid="stat-expiring">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1"><AlertTriangle size={12} className="text-amber-500" /> Expiring Soon</p>
          <p className="text-2xl font-bold text-amber-600">{stats.expiringSoon}</p>
        </Card>
        <Card className="p-4 text-center" data-testid="stat-expired">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1"><XCircle size={12} className="text-red-500" /> Expired</p>
          <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-4">
        <div className="flex rounded-lg border overflow-hidden">
          <button
            onClick={() => setView('gantt')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${view === 'gantt' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            data-testid="view-gantt-btn"
          >
            <BarChart3 size={14} /> Timeline
          </button>
          <button
            onClick={() => setView('calendar')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${view === 'calendar' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            data-testid="view-calendar-btn"
          >
            <Calendar size={14} /> Calendar
          </button>
        </div>
        {view === 'gantt' && (
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search therapists..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="timeline-search-input"
            />
          </div>
        )}
      </div>

      {/* Views */}
      {view === 'gantt' && <GanttTimeline data={timelineData} search={search} />}
      {view === 'calendar' && <CalendarHeatmap calendarData={calendarData} />}
    </div>
  );
}
